import styled from '@emotion/styled';
import { Icon } from 'antd';

import { ComponentTextSmall, dsColors } from 'src/shared/ds';
import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { color, font, layout, mediaQueries } from 'src/shared/styles/variables';

interface BasedOnAuthStatus {
    loggedIn: boolean;
}

export const Content = styled('div')({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
});

/* HEADER */

export const AppHeader = styled('div')({
    position: 'relative',
    backgroundColor: color.white,
    width: '100%',
    height: layout.headerHeight,
    [mediaQueries.sm]: {
        height: 'auto',
    },
});

export const HeaderInnerWrapper = styled('div')({
    height: '100%',
    maxWidth: layout.containerWidth,
    padding: '0 1.5em',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [mediaQueries.sm]: {
        padding: '1rem',
        flexFlow: 'column',
    },
});

export const BrandLogoAnchor = styled('a')({
    display: 'flex',
    alignItems: 'center',
    '&:focus': {
        textDecoration: 'none',
    },
});

interface BrandNameProps {
    blueVariant: boolean;
}

export const BrandName = styled('span')(
    ({ blueVariant }: BrandNameProps) => ({
        textShadow: blueVariant ? 'none' : `0 2px 4px ${hexToRgba(color.black, 0.2)}`,
        marginTop: 10,
        textTransform: 'uppercase',
        letterSpacing: '2.8px',
        color: blueVariant ? color.chathamsBlue : color.white,
        [mediaQueries.md]: {
            display: 'none',
        },
    }),
    font.heavy(font.size.base),
);

export const BrandLogoImage = styled('img')({
    marginRight: 20,
    height: 42,
    [mediaQueries.sm]: {
        height: 34,
        marginRight: 0,
    },
});

export const BrandWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    [mediaQueries.sm]: {
        marginBottom: '1rem',
    },
});

export const UserActionsWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    marginTop: 6,
    height: 30,
    lineHeight: '14px',
});

export const UsernameWrapper = styled('div')({
    height: '100%',
    padding: '0.5rem',
    borderRadius: 4,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
        backgroundColor: color.athensGray,
    },
});

export const UserName = styled('p')(
    {
        margin: '0 0.8rem 0 0.7rem',
        height: '100%',
        textTransform: 'capitalize',
        color: color.blueBayoux,
        maxWidth: '20rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        [mediaQueries.lg]: {
            maxWidth: '15rem',
        },
    },
    font.medium(font.size.base),
);

export const NewUserName = styled(ComponentTextSmall)({
    margin: '0 0.8rem 0 0.7rem',
    height: '100%',
    textTransform: 'capitalize',
    maxWidth: '20rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [mediaQueries.lg]: {
        maxWidth: '15rem',
    },
    color: dsColors.neutral400,
});

export const CaretDownIcon = styled(Icon)({
    '&& svg': {
        width: 10,
        color: color.blueBayoux,
    },
});

/* FOOTER */

export const FooterOuterWrapper = styled('div')({
    borderRadius: 2.6,
    backgroundColor: color.white,
    borderTop: `1px solid ${color.gallery}`,
});

export const FooterInnerWrapper = styled('div')((props: BasedOnAuthStatus) => ({
    maxWidth: props.loggedIn ? layout.containerSmallerWidth : layout.containerBiggerWidth,
    padding: 30,
    margin: '0 auto',
    [mediaQueries.sm]: {
        padding: '30px 2rem',
    },
}));

export const FooterTitle = styled('h3')(
    {
        textTransform: 'uppercase',
        marginBottom: 13,
        lineHeight: 1.5,
        letterSpacing: 4,
        color: color.chathamsBlue,
    },
    font.medium(font.size.large),
);

export const FooterInfoWrapper = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 5,
    [mediaQueries.sm]: {
        flexDirection: 'column',
        marginBottom: 15,
    },
});

export const FooterRight = styled('div')({
    textAlign: 'right',
    [mediaQueries.sm]: {
        textAlign: 'left',
        marginTop: '0.5rem',
    },
});

interface FooterTextProps {
    clickable?: boolean;
}

const FooterTextStyles = {
    lineHeight: 1.8,
    color: color.tundora,
    ...font.book(font.size.medium),
};

export const FooterText = styled('p')(
    (props: FooterTextProps) =>
        props.clickable
            ? {
                  cursor: 'pointer',
                  '&:hover': {
                      color: color.scorpion,
                  },
              }
            : {},
    FooterTextStyles,
);

export const FooterTextAnchor = styled('a')(FooterTextStyles);
